import type { RelatorioAtividadeDto } from '@/modules/reports/application/relatorios-atividades.types';
import {
  DATA_INICIAL_RELATORIOS_ATIVIDADES,
  obterDataInicialNotificacoesRelatorios,
  type PeriodoMesAno,
} from '@/modules/reports/application/relatorios-prazo-habilitacao.utils';

export const STORAGE_KEY_OCULTAS_DEFENSOR = 'relatorios-atividades-notificacoes-ocultas';
export const STORAGE_KEY_OCULTAS_DIRETORIA = 'relatorios-aprovacao-notificacoes-ocultas';

export type RelatoriosNotificacoesModo = 'defensor' | 'diretoria';

export type RelatorioNotificacaoTipo =
  | 'envio'
  | 'feedback'
  | 'correcao'
  | 'validacao_pendente'
  | 'prazo';

export type RelatorioNotificacaoItem = {
  id: string;
  tipo: RelatorioNotificacaoTipo;
  titulo: string;
  mensagem: string;
  data: Date | string;
  modo: RelatoriosNotificacoesModo;
  relatorioId?: number;
  relatorioPeriodo?: string;
  relatorioTipo?: string;
  lida?: boolean;
  linkAprovacao?: boolean;
};

const MESES: Record<number, string> = {
  1: 'janeiro',
  2: 'fevereiro',
  3: 'março',
  4: 'abril',
  5: 'maio',
  6: 'junho',
  7: 'julho',
  8: 'agosto',
  9: 'setembro',
  10: 'outubro',
  11: 'novembro',
  12: 'dezembro',
};

/** Último dia do mês seguinte ao do relatório (prazo máximo de envio). */
export function getUltimoDiaMesSeguinte(mes: number | string, ano: number): Date {
  const m = typeof mes === 'string' ? parseInt(mes, 10) : mes;
  return new Date(ano, m + 1, 0);
}

/** Primeiro dia do mês seguinte (a partir de quando o relatório pode ser enviado). */
export function getPrimeiroDiaMesSeguinte(mes: number | string, ano: number): Date {
  const m = typeof mes === 'string' ? parseInt(mes, 10) : mes;
  if (m === 12) return new Date(ano + 1, 0, 1);
  return new Date(ano, m, 1);
}

export function diasEntre(hoje: Date, data: Date): number {
  const a = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate());
  const b = new Date(data.getFullYear(), data.getMonth(), data.getDate());
  return Math.round((b.getTime() - a.getTime()) / (24 * 60 * 60 * 1000));
}

export function getTipoLabelRelatorio(tipo?: string): string {
  return tipo === 'civil' ? 'Cível' : tipo === 'criminal' ? 'Criminal' : 'N/A';
}

export function loadIdsOcultasParaChave(chave: string): Set<string> {
  try {
    const raw = localStorage.getItem(chave);
    if (!raw) return new Set();
    const arr = JSON.parse(raw) as string[];
    return Array.isArray(arr) ? new Set(arr) : new Set();
  } catch {
    return new Set();
  }
}

export function saveIdsOcultasParaChave(chave: string, ids: Set<string>) {
  try {
    localStorage.setItem(chave, JSON.stringify([...ids]));
  } catch {
    // ignore
  }
}

export function storageKeyOcultasPorModo(modo: RelatoriosNotificacoesModo): string {
  return modo === 'diretoria' ? STORAGE_KEY_OCULTAS_DIRETORIA : STORAGE_KEY_OCULTAS_DEFENSOR;
}

function sortByDataDesc(a: RelatorioNotificacaoItem, b: RelatorioNotificacaoItem): number {
  const dataA = typeof a.data === 'string' ? new Date(a.data).getTime() : (a.data as Date).getTime();
  const dataB = typeof b.data === 'string' ? new Date(b.data).getTime() : (b.data as Date).getTime();
  return dataB - dataA;
}

export function buildNotificacoesDefensor(
  relatorios: RelatorioAtividadeDto[],
  lotacoes: Array<{ defensoria?: unknown } | null | undefined> = [],
): RelatorioNotificacaoItem[] {
  const notificacoes: RelatorioNotificacaoItem[] = [];
  const dataInicialRelatorios: PeriodoMesAno = obterDataInicialNotificacoesRelatorios(
    lotacoes as Array<{ defensoria?: any }>,
  );

  relatorios
    .filter((r) => r.status === 'enviado' && r.dataEnvio)
    .sort((a, b) => {
      const dataA = new Date(a.dataEnvio || 0).getTime();
      const dataB = new Date(b.dataEnvio || 0).getTime();
      return dataB - dataA;
    })
    .slice(0, 5)
    .forEach((relatorio) => {
      notificacoes.push({
        id: `envio-${relatorio.id}`,
        tipo: 'envio',
        titulo: 'Relatório Enviado',
        mensagem: `Relatório ${getTipoLabelRelatorio(relatorio.tipo)} de ${relatorio.periodo || `${relatorio.mes}/${relatorio.ano}`} foi enviado com sucesso.`,
        data: relatorio.dataEnvio || relatorio.dataCriacao || new Date(),
        modo: 'defensor',
        relatorioId: relatorio.id,
        relatorioPeriodo: relatorio.periodo || `${relatorio.mes}/${relatorio.ano}`,
        relatorioTipo: relatorio.tipo,
        lida: false,
      });
    });

  const hoje = new Date();
  const mesAtual = hoje.getMonth() + 1;
  const anoAtual = hoje.getFullYear();

  relatorios
    .filter((r) => r.status === 'rascunho' || r.status === 'pendente')
    .forEach((relatorio) => {
      const mes = typeof relatorio.mes === 'string' ? parseInt(relatorio.mes, 10) : relatorio.mes;
      const ano = relatorio.ano;
      const prazo = getUltimoDiaMesSeguinte(mes, ano);
      const primeiroEnvio = getPrimeiroDiaMesSeguinte(mes, ano);
      const diasRestantes = diasEntre(hoje, prazo);
      const periodo = relatorio.periodo || `${relatorio.mes}/${relatorio.ano}`;
      const prazoStr = prazo.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
      let titulo: string;
      let mensagem: string;
      if (diasRestantes < 0) {
        titulo = 'Prazo encerrado';
        mensagem = `O prazo de envio do relatório ${getTipoLabelRelatorio(relatorio.tipo)} de ${periodo} era até ${prazoStr}. Envie o quanto antes.`;
      } else if (hoje < primeiroEnvio) {
        titulo = 'Prazo de envio';
        const primeiroStr = primeiroEnvio.toLocaleDateString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        });
        mensagem = `Relatório ${getTipoLabelRelatorio(relatorio.tipo)} de ${periodo}: pode ser enviado a partir de ${primeiroStr}. Prazo final: ${prazoStr}.`;
      } else {
        titulo = 'Prazo de envio';
        mensagem =
          diasRestantes === 0
            ? `Último dia! Envie o relatório ${getTipoLabelRelatorio(relatorio.tipo)} de ${periodo} até hoje (${prazoStr}).`
            : `Relatório ${getTipoLabelRelatorio(relatorio.tipo)} de ${periodo}: enviar até ${prazoStr}. Faltam ${diasRestantes} dia(s).`;
      }
      notificacoes.push({
        id: `prazo-${relatorio.id}`,
        tipo: 'prazo',
        titulo,
        mensagem,
        data: hoje,
        modo: 'defensor',
        relatorioId: relatorio.id,
        relatorioPeriodo: periodo,
        relatorioTipo: relatorio.tipo,
        lida: false,
      });
    });

  const periodosComRelatorio = new Set(
    relatorios.map((r) => {
      const m = typeof r.mes === 'string' ? parseInt(r.mes, 10) : r.mes;
      return `${m}-${r.ano}`;
    }),
  );
  for (let y = dataInicialRelatorios.ano; y <= anoAtual; y++) {
    const inicioMes = y === dataInicialRelatorios.ano ? dataInicialRelatorios.mes : 1;
    const fimMes = y === anoAtual ? mesAtual - 1 : 12;
    for (let m = inicioMes; m <= fimMes; m++) {
      const key = `${m}-${y}`;
      if (periodosComRelatorio.has(key)) continue;
      const prazo = getUltimoDiaMesSeguinte(m, y);
      const primeiroEnvio = getPrimeiroDiaMesSeguinte(m, y);
      const diasRestantes = diasEntre(hoje, prazo);
      const periodoStr = `${MESES[m]}/${y}`;
      const prazoStr = prazo.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
      let titulo: string;
      let mensagem: string;
      if (diasRestantes < 0) {
        titulo = 'Prazo encerrado';
        mensagem = `O prazo de envio do relatório de ${periodoStr} era até ${prazoStr}. Você ainda não enviou. Envie o quanto antes.`;
      } else if (hoje < primeiroEnvio) {
        titulo = 'Prazo de envio';
        const primeiroStr = primeiroEnvio.toLocaleDateString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        });
        mensagem = `Relatório de ${periodoStr}: pode ser enviado a partir de ${primeiroStr}. Prazo final: ${prazoStr}.`;
      } else {
        titulo = 'Prazo de envio';
        mensagem =
          diasRestantes === 0
            ? `Último dia! Envie o relatório de ${periodoStr} até hoje (${prazoStr}).`
            : `Relatório de ${periodoStr}: enviar até ${prazoStr}. Faltam ${diasRestantes} dia(s).`;
      }
      notificacoes.push({
        id: `prazo-mes-${m}-${y}`,
        tipo: 'prazo',
        titulo,
        mensagem,
        data: hoje,
        modo: 'defensor',
        relatorioPeriodo: periodoStr,
        lida: false,
      });
    }
  }

  relatorios
    .filter(
      (r) =>
        (r.status === 'aprovado' && r.dataAprovacao) ||
        (r.status === 'reprovado' && r.dataReprovacao),
    )
    .sort((a, b) => {
      const dataA = new Date((a.dataAprovacao || a.dataReprovacao || 0) as string).getTime();
      const dataB = new Date((b.dataAprovacao || b.dataReprovacao || 0) as string).getTime();
      return dataB - dataA;
    })
    .slice(0, 5)
    .forEach((relatorio) => {
      const periodo = relatorio.periodo || `${relatorio.mes}/${relatorio.ano}`;
      if (relatorio.status === 'aprovado') {
        const avalCorregedoria = relatorio.corregedoriaAprovadoEm
          ? ` Aval final da Corregedoria registrado.`
          : ' Aguardando aval da Corregedoria.';
        notificacoes.push({
          id: `feedback-aprovado-${relatorio.id}`,
          tipo: 'feedback',
          titulo: 'Relatório Aprovado',
          mensagem: `Relatório ${getTipoLabelRelatorio(relatorio.tipo)} de ${periodo} foi aprovado pelo diretor.${avalCorregedoria}`,
          data: relatorio.dataAprovacao || relatorio.dataCriacao || new Date(),
          modo: 'defensor',
          relatorioId: relatorio.id,
          relatorioPeriodo: periodo,
          relatorioTipo: relatorio.tipo,
          lida: false,
        });
      } else {
        notificacoes.push({
          id: `feedback-reprovado-${relatorio.id}`,
          tipo: 'feedback',
          titulo: 'Relatório Reprovado',
          mensagem: `Relatório ${getTipoLabelRelatorio(relatorio.tipo)} de ${periodo} foi reprovado.${relatorio.justificativaReprovacao ? ` Justificativa: ${relatorio.justificativaReprovacao}` : ''}`,
          data: relatorio.dataReprovacao || relatorio.dataCriacao || new Date(),
          modo: 'defensor',
          relatorioId: relatorio.id,
          relatorioPeriodo: periodo,
          relatorioTipo: relatorio.tipo,
          lida: false,
        });
      }
    });

  notificacoes.sort(sortByDataDesc);
  return notificacoes.slice(0, 10);
}

export function buildNotificacoesDiretoria(
  lista: RelatorioAtividadeDto[],
  pendentesAprovacao: number,
  diretoriaId: number | null,
): RelatorioNotificacaoItem[] {
  const notificacoes: RelatorioNotificacaoItem[] = [];
  const hoje = new Date();

  if (diretoriaId != null && pendentesAprovacao > 0) {
    notificacoes.push({
      id: 'validacao-pendente',
      tipo: 'validacao_pendente',
      titulo: 'Validação pendente',
      mensagem: `Há ${pendentesAprovacao} relatório(s) aguardando sua aprovação como diretor(a) da diretoria regional.`,
      data: new Date(),
      modo: 'diretoria',
      lida: false,
      linkAprovacao: true,
    });
  }

  lista
    .filter((r) => r.status === 'enviado' && r.dataEnvio)
    .sort((a, b) => {
      const dataA = new Date(a.dataEnvio || 0).getTime();
      const dataB = new Date(b.dataEnvio || 0).getTime();
      return dataB - dataA;
    })
    .slice(0, 6)
    .forEach((relatorio) => {
      const periodo = relatorio.periodo || `${relatorio.mes}/${relatorio.ano}`;
      const def = relatorio.defensorNome?.trim() || 'Defensor';
      notificacoes.push({
        id: `diretoria-envio-${relatorio.id}`,
        tipo: 'envio',
        titulo: 'Relatório aguardando aprovação',
        mensagem: `${def} enviou o relatório ${getTipoLabelRelatorio(relatorio.tipo)} de ${periodo} para análise da diretoria.`,
        data: relatorio.dataEnvio || relatorio.dataCriacao || new Date(),
        modo: 'diretoria',
        relatorioId: relatorio.id,
        relatorioPeriodo: relatorio.periodo || `${relatorio.mes}/${relatorio.ano}`,
        relatorioTipo: relatorio.tipo,
        lida: false,
      });
    });

  lista
    .filter((r) => r.status === 'rascunho' || r.status === 'pendente')
    .forEach((relatorio) => {
      const mes = typeof relatorio.mes === 'string' ? parseInt(relatorio.mes, 10) : relatorio.mes;
      const ano = relatorio.ano;
      const prazo = getUltimoDiaMesSeguinte(mes, ano);
      const primeiroEnvio = getPrimeiroDiaMesSeguinte(mes, ano);
      const diasRestantes = diasEntre(hoje, prazo);
      const periodo = relatorio.periodo || `${relatorio.mes}/${relatorio.ano}`;
      const prazoStr = prazo.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
      const def = relatorio.defensorNome?.trim() || 'Defensor';
      let titulo: string;
      let mensagem: string;
      if (diasRestantes < 0) {
        titulo = 'Prazo encerrado (defensor)';
        mensagem = `Prazo de envio do relatório ${getTipoLabelRelatorio(relatorio.tipo)} de ${periodo} (${def}) era até ${prazoStr}.`;
      } else if (hoje < primeiroEnvio) {
        titulo = 'Prazo de envio (defensor)';
        const primeiroStr = primeiroEnvio.toLocaleDateString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        });
        mensagem = `Relatório ${getTipoLabelRelatorio(relatorio.tipo)} de ${periodo} — ${def}: envio a partir de ${primeiroStr}. Prazo final: ${prazoStr}.`;
      } else {
        titulo = 'Prazo de envio (defensor)';
        mensagem =
          diasRestantes === 0
            ? `Último dia para ${def} enviar o relatório ${getTipoLabelRelatorio(relatorio.tipo)} de ${periodo} (${prazoStr}).`
            : `Relatório ${getTipoLabelRelatorio(relatorio.tipo)} de ${periodo} — ${def}: enviar até ${prazoStr}. Faltam ${diasRestantes} dia(s).`;
      }
      notificacoes.push({
        id: `diretoria-prazo-${relatorio.id}`,
        tipo: 'prazo',
        titulo,
        mensagem,
        data: hoje,
        modo: 'diretoria',
        relatorioId: relatorio.id,
        relatorioPeriodo: periodo,
        relatorioTipo: relatorio.tipo,
        lida: false,
      });
    });

  lista
    .filter(
      (r) =>
        (r.status === 'aprovado' && r.dataAprovacao) ||
        (r.status === 'reprovado' && r.dataReprovacao),
    )
    .sort((a, b) => {
      const dataA = new Date((a.dataAprovacao || a.dataReprovacao || 0) as string).getTime();
      const dataB = new Date((b.dataAprovacao || b.dataReprovacao || 0) as string).getTime();
      return dataB - dataA;
    })
    .slice(0, 5)
    .forEach((relatorio) => {
      const periodo = relatorio.periodo || `${relatorio.mes}/${relatorio.ano}`;
      const def = relatorio.defensorNome?.trim() || 'Defensor';
      if (relatorio.status === 'aprovado') {
        const avalCorregedoria = relatorio.corregedoriaAprovadoEm
          ? ' Aval final da Corregedoria registrado.'
          : ' Aguardando aval da Corregedoria.';
        notificacoes.push({
          id: `diretoria-feedback-aprovado-${relatorio.id}`,
          tipo: 'feedback',
          titulo: 'Relatório aprovado pela diretoria',
          mensagem: `Relatório ${getTipoLabelRelatorio(relatorio.tipo)} de ${periodo} (${def}) foi aprovado pela diretoria.${avalCorregedoria}`,
          data: relatorio.dataAprovacao || relatorio.dataCriacao || new Date(),
          modo: 'diretoria',
          relatorioId: relatorio.id,
          relatorioPeriodo: periodo,
          relatorioTipo: relatorio.tipo,
          lida: false,
        });
      } else {
        const origem = relatorio.dataAprovacao ? 'Corregedoria' : 'Diretoria';
        notificacoes.push({
          id: `diretoria-feedback-reprovado-${relatorio.id}`,
          tipo: 'feedback',
          titulo: 'Relatório reprovado',
          mensagem: `Relatório ${getTipoLabelRelatorio(relatorio.tipo)} de ${periodo} (${def}) foi reprovado (${origem}).${relatorio.justificativaReprovacao ? ` Justificativa: ${relatorio.justificativaReprovacao}` : ''}`,
          data: relatorio.dataReprovacao || relatorio.dataCriacao || new Date(),
          modo: 'diretoria',
          relatorioId: relatorio.id,
          relatorioPeriodo: periodo,
          relatorioTipo: relatorio.tipo,
          lida: false,
        });
      }
    });

  const validacao = notificacoes.filter((n) => n.tipo === 'validacao_pendente');
  const resto = notificacoes.filter((n) => n.tipo !== 'validacao_pendente').sort(sortByDataDesc).slice(0, 10);
  return [...validacao, ...resto];
}

/** Mantido para compatibilidade; data inicial legado quando sem lotação. */
export { DATA_INICIAL_RELATORIOS_ATIVIDADES };
